import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, Settings, ShieldCheck, X } from "lucide-react";

export default function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Custom states for cookies
  const [analyticsConsent, setAnalyticsConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);

  useEffect(() => {
    // Check if consent has already been given
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      // Delay showing the banner slightly for a smoother UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consentValue = {
      analytics: true,
      marketing: true,
      strictlyNecessary: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem("cookieConsent", JSON.stringify(consentValue));
    setIsVisible(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const consentValue = {
      analytics: false,
      marketing: false,
      strictlyNecessary: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem("cookieConsent", JSON.stringify(consentValue));
    setIsVisible(false);
    setShowSettings(false);
  };

  const handleSaveSettings = () => {
    const consentValue = {
      analytics: analyticsConsent,
      marketing: marketingConsent,
      strictlyNecessary: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem("cookieConsent", JSON.stringify(consentValue));
    setIsVisible(false);
    setShowSettings(false);
  };

  const handleOpenSettings = () => {
    // Sync current state if cookie consent is already stored
    const existingConsent = localStorage.getItem("cookieConsent");
    if (existingConsent) {
      try {
        const parsed = JSON.parse(existingConsent);
        setAnalyticsConsent(!!parsed.analytics);
        setMarketingConsent(!!parsed.marketing);
      } catch (e) {
        console.error("Error parsing cookie consent", e);
      }
    }
    setShowSettings(true);
  };

  if (!isVisible && !showSettings) return null;

  return (
    <>
      {/* 1. Main Floating Cookie Banner */}
      {isVisible && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md lg:max-w-lg z-50 animate-fade-in-up font-sans">
          <div className="bg-neutral-900 text-white rounded-2xl p-5 md:p-6 shadow-2xl border border-neutral-800 flex flex-col gap-4 backdrop-blur-md bg-opacity-95">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-[#156E77] bg-opacity-15 rounded-xl text-[#156E77] flex-shrink-0">
                <Cookie className="h-6 w-6 stroke-[2]" />
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-semibold tracking-wide text-neutral-100 mb-1 flex items-center gap-2">
                  We value your privacy
                </h4>
                <p className="text-sm text-neutral-400 leading-relaxed">
                  We use cookies to improve your experience, analyze traffic, and personalize content.
                  Non-essential cookies will only be used with your consent. Learn more in our{" "}
                  <Link to="/privacy-policy" className="text-teal-400 hover:text-teal-300 hover:underline font-medium">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-neutral-800">
              <button
                onClick={handleAcceptAll}
                className="flex-1 min-w-[120px] bg-[#F47321] hover:bg-[#d85d10] text-white text-sm font-semibold py-2.5 px-4 rounded-xl transition-all duration-200 active:scale-[0.98] shadow-lg shadow-[#F47321]/15"
              >
                Accept All
              </button>

              <button
                onClick={handleRejectAll}
                className="flex-1 min-w-[120px] bg-neutral-800 hover:bg-neutral-700 text-neutral-200 text-sm font-semibold py-2.5 px-4 rounded-xl transition-all duration-200 active:scale-[0.98] border border-neutral-700"
              >
                Reject Non-Essential
              </button>

              <button
                onClick={handleOpenSettings}
                className="flex items-center justify-center gap-1.5 p-2.5 text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800 rounded-xl transition-all duration-200"
                title="Cookie Settings"
              >
                <Settings className="h-5 w-5" />
                <span className="text-sm font-medium md:hidden lg:inline">Settings</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. Premium Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 font-sans">
          <div className="bg-white text-neutral-900 rounded-2xl shadow-2xl p-6 max-w-md w-full border border-neutral-100 flex flex-col gap-6 animate-scale-in">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-neutral-100 pb-4">
              <div className="flex items-center gap-2.5">
                <ShieldCheck className="h-6 w-6 text-[#156E77]" />
                <h3 className="text-xl font-bold tracking-tight text-neutral-900">
                  Cookie Preferences
                </h3>
              </div>
              <button
                onClick={() => {
                  // If cookieConsent doesn't exist in localStorage, close modal and show banner
                  if (!localStorage.getItem("cookieConsent")) {
                    setIsVisible(true);
                  }
                  setShowSettings(false);
                }}
                className="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Privacy Policy notice in modal */}
            <p className="text-xs text-neutral-500 -mt-2 leading-relaxed">
              We respect your privacy. You can control how cookies are used on this site. Read our full{" "}
              <Link to="/privacy-policy" className="text-[#156E77] hover:underline font-semibold">
                Privacy Policy
              </Link>
              .
            </p>

            {/* List of Preferences */}
            <div className="flex flex-col gap-4 py-2">
              {/* Strictly Necessary */}
              <div className="flex items-start justify-between gap-4 p-3 bg-neutral-50 rounded-xl">
                <div className="flex-1">
                  <h5 className="text-sm font-semibold text-neutral-800 mb-0.5">
                    Strictly Necessary
                  </h5>
                  <p className="text-xs text-neutral-500 leading-relaxed">
                    Required for the website to function correctly. Cannot be disabled.
                  </p>
                </div>
                <div className="flex items-center h-full pt-1">
                  {/* Custom Toggle Styled (Checked & Disabled) */}
                  <span className="relative inline-flex h-6 w-10 items-center rounded-full bg-[#156E77] opacity-60 cursor-not-allowed">
                    <span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-5" />
                  </span>
                </div>
              </div>

              {/* Analytics */}
              <div className="flex items-start justify-between gap-4 p-3 hover:bg-neutral-50 rounded-xl transition-colors">
                <div className="flex-1">
                  <h5 className="text-sm font-semibold text-neutral-800 mb-0.5">
                    Analytics Cookies
                  </h5>
                  <p className="text-xs text-neutral-500 leading-relaxed">
                    Help us analyze traffic, see how users interact, and identify areas for improvement.
                  </p>
                </div>
                <div className="flex items-center h-full pt-1">
                  {/* Custom Toggle Switch */}
                  <button
                    onClick={() => setAnalyticsConsent(!analyticsConsent)}
                    type="button"
                    className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors focus:outline-none ${analyticsConsent ? "bg-[#156E77]" : "bg-neutral-300"
                      }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${analyticsConsent ? "translate-x-5" : "translate-x-1"
                        }`}
                    />
                  </button>
                </div>
              </div>

              {/* Marketing */}
              <div className="flex items-start justify-between gap-4 p-3 hover:bg-neutral-50 rounded-xl transition-colors">
                <div className="flex-1">
                  <h5 className="text-sm font-semibold text-neutral-800 mb-0.5">
                    Marketing Cookies
                  </h5>
                  <p className="text-xs text-neutral-500 leading-relaxed">
                    Used to show relevant advertisements and measure campaign effectiveness.
                  </p>
                </div>
                <div className="flex items-center h-full pt-1">
                  {/* Custom Toggle Switch */}
                  <button
                    onClick={() => setMarketingConsent(!marketingConsent)}
                    type="button"
                    className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors focus:outline-none ${marketingConsent ? "bg-[#156E77]" : "bg-neutral-300"
                      }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${marketingConsent ? "translate-x-5" : "translate-x-1"
                        }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="flex items-center gap-3 border-t border-neutral-100 pt-4 mt-2">
              <button
                onClick={() => {
                  // If cookieConsent doesn't exist in localStorage, close modal and show banner
                  if (!localStorage.getItem("cookieConsent")) {
                    setIsVisible(true);
                  }
                  setShowSettings(false);
                }}
                className="flex-1 py-2.5 px-4 border border-neutral-200 hover:bg-neutral-50 text-neutral-700 text-sm font-semibold rounded-xl transition-colors active:scale-[0.98]"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSettings}
                className="flex-1 py-2.5 px-4 bg-[#156E77] hover:bg-[#0f535a] text-white text-sm font-semibold rounded-xl transition-colors active:scale-[0.98] shadow-lg shadow-[#156E77]/10"
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
